/**
 * Full-canvas avalanche used as a post-result crash presentation.
 *
 * The effect is deliberately procedural: it stays crisp at any canvas size
 * and does not add another large raster asset to the loading path.
 */
export class AvalancheEffect {
  private width: number;
  private height: number;
  private elapsedMs = 0;
  private active = false;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  begin() {
    this.elapsedMs = 0;
    this.active = true;
  }

  reset() {
    this.elapsedMs = 0;
    this.active = false;
  }

  resize(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  update(dtMs: number) {
    if (!this.active) return;
    this.elapsedMs += Math.max(0, Math.min(dtMs, 100));
  }

  isActive() { return this.active; }

  /** The wave reaches the racers at roughly 250ms and clears the view by 1.5s. */
  getProgress() { return Math.min(1, this.elapsedMs / 1500); }

  draw(ctx: CanvasRenderingContext2D) {
    if (!this.active) return;

    const progress = this.getProgress();
    const travel = 1 - Math.pow(1 - progress, 2.25);
    const frontY = -135 + travel * (this.height + 285);
    const points = 18;
    const waveY = (i: number) => {
      const x = (i / points) * this.width;
      return frontY
        + Math.sin(i * 1.73 + this.elapsedMs * 0.008) * 15
        + Math.sin(x * 0.025 - this.elapsedMs * 0.004) * 9;
    };

    ctx.save();

    // Cold shadow beneath the breaking edge gives the powder wall its depth.
    const shadow = ctx.createLinearGradient(0, frontY - 30, 0, frontY + 75);
    shadow.addColorStop(0, 'rgba(89,133,166,0)');
    shadow.addColorStop(0.48, 'rgba(89,133,166,0.34)');
    shadow.addColorStop(1, 'rgba(89,133,166,0)');
    ctx.fillStyle = shadow;
    ctx.fillRect(0, frontY - 40, this.width, 125);

    // Main mass: everything uphill of the irregular leading edge is engulfed.
    const snow = ctx.createLinearGradient(0, Math.max(0, frontY - 260), 0, frontY + 25);
    snow.addColorStop(0, 'rgba(239,247,252,0.97)');
    snow.addColorStop(0.72, 'rgba(217,235,247,0.98)');
    snow.addColorStop(1, 'rgba(250,253,255,1)');
    ctx.fillStyle = snow;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(this.width, 0);
    for (let i = points; i >= 0; i--) ctx.lineTo((i / points) * this.width, waveY(i));
    ctx.closePath();
    ctx.fill();

    // Rounded chunks along the front stop it reading as a flat screen wipe.
    for (let i = 0; i < 30; i++) {
      const phase = (i * 0.61803398875 + this.elapsedMs * (0.00010 + (i % 4) * 0.000025)) % 1;
      const x = phase * (this.width + 100) - 50;
      const localI = Math.max(0, Math.min(points, Math.round((x / this.width) * points)));
      const y = waveY(localI) + Math.sin(i * 4.17) * 24 - 8;
      const radius = 8 + (i % 6) * 3.4;
      ctx.globalAlpha = 0.35 + (i % 5) * 0.10;
      ctx.fillStyle = i % 3 === 0 ? '#cfe4f2' : '#f8fcff';
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }

    // Fine airborne powder trails slightly ahead of the dense wave.
    ctx.globalAlpha = Math.min(0.72, progress * 2.2);
    for (let i = 0; i < 42; i++) {
      const x = ((i * 97.13 + this.elapsedMs * (0.035 + (i % 3) * 0.012)) % (this.width + 80)) - 40;
      const y = frontY + 24 + ((i * 43) % 100);
      const radius = 1.5 + (i % 4) * 0.9;
      ctx.fillStyle = i % 2 ? '#ffffff' : '#c8e0ef';
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
